import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { authAPI } from '../lib/api';
import {
  EnvelopeIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const setAuth = useAuthStore((state) => state.setAuth);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: authAPI.login,
    onSuccess: (data) => {
      setErrorMessage(null);
      setAuth(data.token, data.user);
      navigate(redirectUrl);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Sign in failed');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(formData);
  };

  return (
    <>
      <Helmet>
        <title>Sign In | GetBookReviews</title>
        <meta name="description" content="Sign in to GetBookReviews and manage your books and reviews." />
      </Helmet>

      <div className="min-h-screen bg-dark-950 bg-book-pattern flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src="/brand/logo-book-96.png" alt="" width={48} height={48} className="w-12 h-12" />
              <span className="text-2xl font-serif font-bold gradient-text">
                GetBookReviews
              </span>
            </Link>
          </div>

          {/* Card */}
          <div className="card-dark">
            <h1 className="text-2xl font-serif font-bold text-white text-center mb-2">
              Welcome back
            </h1>
            <p className="text-gray-400 text-center mb-8">
              Sign in to continue
            </p>

            {errorMessage && (
              <div className="p-4 mb-6 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <div className="flex items-center gap-2">
                  <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{errorMessage}</span>
                </div>
                {errorMessage.toLowerCase().includes('verify') && (
                  <Link 
                    to="/resend-verification" 
                    className="block mt-2 text-sm text-primary-400 hover:text-primary-300 underline"
                  >
                    Resend verification email
                  </Link>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <EnvelopeIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-400">
                  <input type="checkbox" className="rounded border-gray-600 bg-dark-800 text-primary-500 focus:ring-primary-500" />
                  Remember me
                </label>
                <Link to="/forgot-password" className="text-primary-400 hover:text-primary-300">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-400 hover:text-primary-300 font-medium">
                  Register now
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
